import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@quintoandar/cozy-core/Box';
import Spinner from '@quintoandar/cozy-core/Spinner';

import { getVisitByCode } from 'api/providers/visitAPI/VisitV3API';
import ROUTES from 'routes/constants';
import { getDateEpochFromStartTimeInSec } from 'utils/date';

const REQUESTED_FIELDS = [
  'ACTOR_DETAILS',
  'FOLLOW_UP',
  'HOUSE_DETAILS',
  'STRUCTURED_STATUS',
  'TEMP_MIGRATION_SUPPORT',
  'STATUS_LOG',
];

const getVisitDetails = (visit) => visit?.singleHouseVisitDetails || {};

const getVisitDetailPath = ({ extraQueryParams = {}, visit, visitCode }) => {
  const details = getVisitDetails(visit);
  const visitTime = details.visitTime || {};
  const houseDetails = details.houseDetails || {};
  const houseId = houseDetails.houseId || houseDetails.id || houseDetails.code;

  if (!houseId || !visitTime.date) {
    const params = new URLSearchParams({ visitCode, ...extraQueryParams });
    return `${ROUTES.AGENDA}?${params.toString()}`;
  }

  const dateEpoch = getDateEpochFromStartTimeInSec(
    visitTime.beginHour || visitTime.startTime || '00:00',
    visitTime.date,
  );
  const params = new URLSearchParams({ visitCode, ...extraQueryParams });

  return `${ROUTES.AGENDA_VISIT_DETAIL.replace(
    ':dateEpoch',
    dateEpoch,
  ).replace(':houseId', houseId)}?${params.toString()}`;
};

const getExtraQueryParams = ({ location, params }) => {
  const currentParams = new URLSearchParams(location.search || '');
  const extraQueryParams = {};

  ['backTo', 'origin'].forEach((key) => {
    const value = currentParams.get(key);
    if (value) extraQueryParams[key] = value;
  });

  if ((location.pathname || '').includes('/finalizar/')) {
    extraQueryParams.openFinishVisit = 'true';
    if (params.result) extraQueryParams.finishVisitResult = params.result;
  }

  if ((location.pathname || '').includes('/cancelar/')) {
    extraQueryParams.openCancelVisit = 'true';
  }

  return extraQueryParams;
};

const VisitDeepLinkRedirect = ({ location, params, router }) => {
  const [hasError, setHasError] = useState(false);
  const visitCode = params.visitCode;

  useEffect(() => {
    let isMounted = true;

    const redirect = async () => {
      try {
        const visit = await getVisitByCode({
          requestedFields: REQUESTED_FIELDS,
          visitCode,
        });

        if (!isMounted) return;

        router.replace(
          getVisitDetailPath({
            extraQueryParams: getExtraQueryParams({ location, params }),
            visit,
            visitCode,
          }),
        );
      } catch (error) {
        if (!isMounted) return;

        setHasError(true);
        const fallbackParams = new URLSearchParams({ visitCode });
        router.replace(`${ROUTES.AGENDA}?${fallbackParams.toString()}`);
      }
    };

    redirect();

    return () => {
      isMounted = false;
    };
  }, [location, params, router, visitCode]);

  if (hasError) return null;

  return (
    <Box display="flex" justifyContent="center" mt={4}>
      <Spinner />
    </Box>
  );
};

VisitDeepLinkRedirect.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
  params: PropTypes.shape({
    result: PropTypes.string,
    visitCode: PropTypes.string.isRequired,
  }).isRequired,
  router: PropTypes.shape({
    replace: PropTypes.func.isRequired,
  }).isRequired,
};

export default VisitDeepLinkRedirect;
